import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

export default function Navigation() {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#menu', label: t('nav.menu') },
    { href: '#reservations', label: t('nav.reservations') },
    { href: '#reviews', label: t('nav.reviews') },
    { href: '#location', label: t('nav.location') },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#FBF9F6]/95 backdrop-blur-xl shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => scrollToSection(e, '#home')}
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <span className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-[#2A1E17]">
              Rui's
            </span>
            <span className={`hidden sm:block text-xs uppercase tracking-[0.2em] ${isScrolled ? 'text-[#6B584B]' : 'text-[#FBF9F6]/80'}`}>
              Restaurante
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className={`text-sm font-medium tracking-wide uppercase transition-colors ${
                  isScrolled
                    ? 'text-[#2A1E17] hover:text-[#8C5A2B]'
                    : 'text-[#FBF9F6] hover:text-[#E6D8C8]'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-all ${
                isScrolled
                  ? 'text-[#2A1E17] hover:bg-[#F0EAE1]'
                  : 'text-[#FBF9F6] hover:bg-white/10'
              }`}
            >
              <Globe size={18} strokeWidth={1.5} />
              <span className="uppercase">{language}</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className={`lg:hidden p-2 transition-colors ${
                isScrolled ? 'text-[#2A1E17]' : 'text-[#FBF9F6]'
              }`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FBF9F6] border-t border-[#E6D8C8]"
        >
          <div className="px-6 py-4 space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                className="block py-3 text-[#2A1E17] text-sm font-medium tracking-wide uppercase hover:text-[#8C5A2B] transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
